import { ref } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import useConfig from '@/composables/useConfig';
import useMqtt from '@/composables/useMqtt';
import useFlows from '@/composables/useFlows';
import useSequence from '@/composables/useSequence';
import useActions from '@/composables/useActions';
import {
  START_FLOW_ID,
} from '@/js/constants';

const commandTypes = {
  CONNECTION_CHAT_REQUEST: 'CONNECTION_CHAT_REQUEST',
  CONNECTION_RESPONSE: 'CONNECTION_RESPONSE',
  CONNECTION_DECLINE: 'CONNECTION_DECLINE',
  ROBOT_ACTION: 'ROBOT_ACTION',
  END_CHAT: 'END_CHAT',
};
const ROBOT_ACTIONS = {
  ROBOT_CHAT_SWITCH: 'ROBOT_CHAT_SWITCH',
  END_CHAT: 'ROBOT_CHAT_END',
};
const PING_MESSAGES = {
  PING: 'ping',
  INTERRUPT: 'interrupt',
};
const COMMUNICATION_TIMEOUT = 2000;
const PING_TIMEOUT = 8000;
const PING_DELAY = 2000;

const { currentConfig, setFlowStateBackToStart } = useConfig();

let communicationTopics = [];
let basePublicTopic;
let frameTopic;
let params;
let isCreator = false;
let noAvailableTimeoutId;
let pingTimeoutId;
let restartPingTimeoutId;
const targets = {
  targetUuid: null,
  sourceUuid: null,
};
const isChatInProgress = ref(false);
const hideAvatarDuringChat = ref(false);

/**
 * A & B robots -> listenRobotChat()
 * A -> executeRobotChat()
 * B -> connectionRequestHandler() -> startPing()
 * A -> connectionResponseHandler() -> startPing() -> continue flow
 * A -> send ROBOT_CHAT_SWITCH action
 * B -> executeRobotChat() { run chat flow }
 * A or B -> send ROBOT_CHAT_END action and stopPing()
 */

export default function useR2RChat() {
  const { languageFlow } = useFlows();
  const { currentAction } = useActions();

  async function cleanupChatData() {
    targets.targetUuid = null;
    targets.sourceUuid = null;
    isChatInProgress.value = false;
    hideAvatarDuringChat.value = false;
    isCreator = false;
    params = {};

    const { mqttUnsubscribe } = useMqtt();
    await Promise.all(
      communicationTopics.map(({ topic, handler }) => mqttUnsubscribe({
        scope: 'public',
        topic,
      }, handler)),
    );
    communicationTopics = [];
  }

  async function noRobotAvailable() {
    const nextId = !isCreator ? START_FLOW_ID : params.unavailableNext;
    const { runSequence, interruptSequence } = useSequence();
    await cleanupChatData();

    if (nextId) {
      await interruptSequence();
      setFlowStateBackToStart(nextId);
      runSequence(nextId);
    }
  }

  function stopPing(notify) {
    clearTimeout(pingTimeoutId);
    clearTimeout(restartPingTimeoutId);
    const { mqttPublish, mqttUnsubscribe } = useMqtt();
    const { sourceUuid, targetUuid } = targets;
    const subscribeTopic = isCreator ? `${targetUuid}/${sourceUuid}` : `${sourceUuid}/${targetUuid}`;
    const publishTopic = isCreator ? `${sourceUuid}/${targetUuid}` : `${targetUuid}/${sourceUuid}`;

    mqttUnsubscribe({ scope: 'public', topic: `${basePublicTopic}/${subscribeTopic}/ping` }, pingHandler); // eslint-disable-line no-use-before-define
    if (notify) {
      mqttPublish(
        { scope: 'public', topic: `${basePublicTopic}/${publishTopic}/ping` },
        JSON.stringify({
          data: PING_MESSAGES.INTERRUPT,
        }),
      );
    }
  }

  function interruptRobot() {
    stopPing(true);
    noRobotAvailable();
  }

  function pingHandler(err, { message }) {
    clearTimeout(pingTimeoutId);
    clearTimeout(restartPingTimeoutId);
    const { data } = JSON.parse(message);

    if (data === PING_MESSAGES.PING) {
      const { mqttPublish } = useMqtt();
      const { sourceUuid, targetUuid } = targets;
      const publishTopic = isCreator ? `${sourceUuid}/${targetUuid}` : `${targetUuid}/${sourceUuid}`;
      restartPingTimeoutId = setTimeout(() => {
        mqttPublish(
          { scope: 'public', topic: `${basePublicTopic}/${publishTopic}/ping` },
          JSON.stringify({
            data: PING_MESSAGES.PING,
          }),
        );
        pingTimeoutId = setTimeout(interruptRobot, PING_TIMEOUT);
      }, PING_DELAY);
    }

    if (data === PING_MESSAGES.INTERRUPT) {
      interruptRobot();
    }
  }

  async function startPing() {
    const { mqttPublish, mqttSubscribe } = useMqtt();
    const { sourceUuid, targetUuid } = targets;
    const subscribeTopic = isCreator ? `${targetUuid}/${sourceUuid}` : `${sourceUuid}/${targetUuid}`;
    const publishTopic = isCreator ? `${sourceUuid}/${targetUuid}` : `${targetUuid}/${sourceUuid}`;

    await Promise.all([
      mqttPublish(
        { scope: 'public', topic: `${basePublicTopic}/${publishTopic}/ping` },
        JSON.stringify({
          data: PING_MESSAGES.PING,
        }),
      ),
      mqttSubscribe({ scope: 'public', topic: `${basePublicTopic}/${subscribeTopic}/ping` }, pingHandler),
    ]);

    communicationTopics.push({
      topic: `${basePublicTopic}/${subscribeTopic}/ping`,
      handler: pingHandler,
    });

    pingTimeoutId = setTimeout(interruptRobot, PING_TIMEOUT);
  }

  async function stopChat() {
    if (!isChatInProgress.value) {
      return;
    }

    stopPing();
    const { interruptSequence, runSequence } = useSequence();

    const { mqttPublish } = useMqtt();
    const { targetUuid, sourceUuid } = targets;
    const publishTopic = isCreator ? `${targetUuid}/${sourceUuid}` : `${sourceUuid}/${targetUuid}`;
    await mqttPublish(
      { scope: 'public', topic: `${basePublicTopic}/${publishTopic}` },
      JSON.stringify({
        type: commandTypes.END_CHAT,
        action: ROBOT_ACTIONS.END_CHAT,
      }),
    );

    const nextSequence = !isCreator ? START_FLOW_ID : null;
    await cleanupChatData();

    if (nextSequence) {
      // restart flow
      await interruptSequence();
      setFlowStateBackToStart(nextSequence);
      runSequence(nextSequence);
    } else {
      // last action will be random flow so we just continue sequence
      currentAction.value.stop();
    }
  }

  /**
   * Robot A receive confirmation response from robot B to start chat
   */
  async function connectionResponseHandler(err, { message }) {
    clearTimeout(noAvailableTimeoutId);
    const { mqttPublish } = useMqtt();
    const { targetUuid } = JSON.parse(message);

    if (targets.targetUuid) {
      const { sourceUuid } = targets;
      await mqttPublish(
        { scope: 'public', topic: `${basePublicTopic}/${targetUuid}/${sourceUuid}` },
        JSON.stringify({
          type: commandTypes.CONNECTION_DECLINE,
        }),
      );
      return;
    }

    targets.targetUuid = targetUuid;
    startPing();

    // finish robotChat action and continue sequence
    currentAction.value.stop();
  }

  /**
   * Robot A init chat
   * @param parameters {Object}
   * @param parameters.targetFrame {string}
   * @param parameters.elementId {string}
   * @param parameters.version {string}
   * @param parameters.unavailableNext {string}
   * @returns {Promise<void>}
   */
  async function executeRobotChat(parameters) {
    params = parameters;
    const { targetFrame, elementId, version } = params;
    const { mqttSubscribe, mqttPublish, mqttUnsubscribe } = useMqtt();
    isCreator = currentConfig.value.frameId !== targetFrame;

    if (!isCreator) {
      isChatInProgress.value = true;
      // finish robotChat action and continue sequence
      currentAction.value.stop();
      return;
    }

    isChatInProgress.value = true;
    hideAvatarDuringChat.value = currentAction.value.hideAvatar;
    const sourceUuid = uuidv4();
    targets.sourceUuid = sourceUuid;

    await Promise.all([
      await mqttPublish(
        { scope: 'public', topic: `${basePublicTopic}/${targetFrame}` },
        JSON.stringify({
          type: commandTypes.CONNECTION_CHAT_REQUEST,
          sourceUuid,
          elementId,
          version,
        }),
      ),
      await mqttSubscribe({ scope: 'public', topic: `${basePublicTopic}/${sourceUuid}` }, connectionResponseHandler),
    ]);

    communicationTopics.push({
      topic: `${basePublicTopic}/${sourceUuid}`,
      handler: connectionResponseHandler,
    });

    noAvailableTimeoutId = setTimeout(() => {
      mqttUnsubscribe({ scope: 'public', topic: `${basePublicTopic}/${sourceUuid}` }, connectionResponseHandler);
      noRobotAvailable();
    }, COMMUNICATION_TIMEOUT);
  }

  async function chatMessageReceivedHandler(err, { message }) {
    const { mqttUnsubscribe } = useMqtt();
    const { type, action } = JSON.parse(message);
    const { targetUuid, sourceUuid } = targets;

    communicationTopics = communicationTopics.filter(({ handler }) => handler !== chatMessageReceivedHandler);
    const unsubscribeTopic = isCreator ? `${sourceUuid}/${targetUuid}` : `${targetUuid}/${sourceUuid}`;
    await mqttUnsubscribe({ scope: 'public', topic: `${basePublicTopic}/${unsubscribeTopic}` }, chatMessageReceivedHandler);

    if (type === commandTypes.CONNECTION_DECLINE) {
      stopPing();
      return;
    }

    if (!isChatInProgress.value) {
      const { runSequence, interruptSequence } = useSequence();
      await interruptSequence();
      setFlowStateBackToStart(params.elementId);
      runSequence(params.elementId);
    }

    if (type === commandTypes.ROBOT_ACTION && action === ROBOT_ACTIONS.ROBOT_CHAT_SWITCH) {
      // finish robotAction action and continue sequence
      currentAction.value.stop();
    } else if (type === commandTypes.END_CHAT) {
      await stopChat();
    }
  }

  async function switchRobot() {
    const { mqttSubscribe, mqttPublish } = useMqtt();

    const { sourceUuid, targetUuid } = targets;
    const publishTopic = isCreator ? `${targetUuid}/${sourceUuid}` : `${sourceUuid}/${targetUuid}`;
    const subscribeTopic = isCreator ? `${sourceUuid}/${targetUuid}` : `${targetUuid}/${sourceUuid}`;
    await Promise.all([
      mqttPublish(
        { scope: 'public', topic: `${basePublicTopic}/${publishTopic}` },
        JSON.stringify({
          type: commandTypes.ROBOT_ACTION,
          action: ROBOT_ACTIONS.ROBOT_CHAT_SWITCH,
        }),
      ),
      mqttSubscribe(
        { scope: 'public', topic: `${basePublicTopic}/${subscribeTopic}` },
        chatMessageReceivedHandler,
      ),
    ]);

    communicationTopics.push({
      topic: `${basePublicTopic}/${subscribeTopic}`,
      handler: chatMessageReceivedHandler,
    });
  }

  /**
   * Robot B receive request to start chat
   * @param err
   * @param message
   * @returns {Promise<void>}
   */
  async function connectionRequestHandler(err, { message }) {
    // ignore if robot is already chatting
    if (isChatInProgress.value) {
      return;
    }

    const { sourceUuid, elementId, version, type } = JSON.parse(message);
    // ignore if version is invalid
    if (languageFlow.value[elementId]?.[0]?.params.version !== version) {
      return;
    }

    if (type === commandTypes.CONNECTION_CHAT_REQUEST) {
      const targetUuid = uuidv4();
      const { mqttPublish, mqttSubscribe } = useMqtt();
      targets.targetUuid = targetUuid;
      targets.sourceUuid = sourceUuid;
      await Promise.all([
        mqttPublish(
          { scope: 'public', topic: `${basePublicTopic}/${sourceUuid}` },
          JSON.stringify({
            type: commandTypes.CONNECTION_RESPONSE,
            targetUuid,
          }),
        ),
        mqttSubscribe({ scope: 'public', topic: `${basePublicTopic}/${targetUuid}/${sourceUuid}` }, chatMessageReceivedHandler),
      ]);

      communicationTopics.push({
        topic: `${basePublicTopic}/${targetUuid}/${sourceUuid}`,
        handler: chatMessageReceivedHandler,
      });

      startPing();
      params = { elementId };
    }
  }

  /**
   * Both robots A and B
   * @returns {Promise<void>}
   */
  async function listenRobotChat() {
    const { externalCommands } = currentConfig.value;

    if (externalCommands.robotChat) {
      basePublicTopic = `public/${currentConfig.value.projectId}`;
      frameTopic = `${basePublicTopic}/${currentConfig.value.frameId}`;
      const { mqttSubscribe } = useMqtt();
      await mqttSubscribe({ scope: 'public', topic: frameTopic }, connectionRequestHandler);
    }
  }

  return {
    isChatInProgress,
    hideAvatarDuringChat,
    listenRobotChat,
    executeRobotChat,
    switchRobot,
    stopChat,
    ROBOT_ACTIONS,
  };
}
